import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Mic, MicOff, Square, X, Send, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * VoiceInput Component
 * Provides speech-to-text functionality using Web Speech API
 * with fallback handling for unsupported browsers.
 */
const VoiceInput = ({ 
  onTranscript, 
  onSend, 
  disabled = false,
  autoSend = false,
  className = '' 
}) => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(true);
  const [error, setError] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  
  const recognitionRef = useRef(null);
  const silenceTimeoutRef = useRef(null);
  const hasResultRef = useRef(false);

  // Check for Web Speech API support
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setIsSupported(false);
    }
  }, []);

  // Initialize speech recognition
  const initRecognition = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return null;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
      hasResultRef.current = false;
    };

    recognition.onresult = (event) => {
      let interim = '';
      let final = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          final += result[0].transcript;
          hasResultRef.current = true;
        } else {
          interim += result[0].transcript;
        }
      }

      if (final) {
        setTranscript(prev => prev + final);
        setInterimTranscript('');
        
        // Reset silence timeout when we get final results
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
        }
        
        // Auto-send after 2 seconds of silence if enabled
        if (autoSend) {
          silenceTimeoutRef.current = setTimeout(() => {
            handleAutoSend();
          }, 2000);
        }
      } else {
        setInterimTranscript(interim);
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      
      switch (event.error) {
        case 'not-allowed':
        case 'permission-denied':
          setPermissionDenied(true);
          setError('Microphone access denied. Please allow microphone access in your browser settings.');
          toast.error('Microphone access denied');
          break;
        case 'no-speech':
          // Ignore no-speech errors, just restart if still listening
          if (isListening) {
            try {
              recognition.start();
            } catch (e) {
              // Ignore if already started
            }
          }
          return;
        case 'audio-capture':
          setError('No microphone found. Please connect a microphone.');
          toast.error('No microphone found');
          break;
        case 'network':
          setError('Network error. Please check your connection.');
          toast.error('Network error during speech recognition');
          break;
        default:
          setError('Speech recognition error. Please try again.');
      }
      
      setIsListening(false);
    };

    recognition.onend = () => {
      // If we're still supposed to be listening but recognition ended, restart
      if (isListening && !error && !permissionDenied) {
        try {
          recognition.start();
        } catch (e) {
          setIsListening(false);
        }
      } else {
        setIsListening(false);
      }
    };

    return recognition;
  }, [autoSend, isListening, error, permissionDenied]);

  // Handle auto-send
  const handleAutoSend = useCallback(() => {
    if (transcript.trim() && hasResultRef.current) {
      stopListening();
      setTimeout(() => {
        onSend?.(transcript.trim());
        setTranscript('');
        setInterimTranscript('');
      }, 100);
    }
  }, [transcript, onSend]);

  // Start listening
  const startListening = useCallback(() => {
    if (disabled || permissionDenied) return;
    
    setError(null);
    setTranscript('');
    setInterimTranscript('');
    hasResultRef.current = false;

    if (!recognitionRef.current) {
      recognitionRef.current = initRecognition();
    }

    if (recognitionRef.current) {
      try {
        recognitionRef.current.start();
      } catch (e) {
        // Handle if recognition is already started
        if (e.message.includes('already started')) {
          recognitionRef.current.stop();
          setTimeout(() => {
            try {
              recognitionRef.current?.start();
            } catch (err) {
              console.error('Failed to restart recognition:', err);
            }
          }, 100);
        }
      }
    }
  }, [disabled, permissionDenied, initRecognition]);

  // Stop listening
  const stopListening = useCallback(() => {
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
    }
    
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignore errors when stopping
      }
    }
    setIsListening(false);
  }, []);

  // Cancel and clear
  const cancelListening = useCallback(() => {
    stopListening();
    setTranscript('');
    setInterimTranscript('');
    setError(null);
  }, [stopListening]);

  // Manual send
  const handleManualSend = useCallback(() => {
    if (transcript.trim()) {
      stopListening();
      onSend?.(transcript.trim());
      setTranscript('');
      setInterimTranscript('');
    }
  }, [transcript, onSend, stopListening]);

  // Pass transcript to parent for editing
  useEffect(() => {
    if (onTranscript) {
      onTranscript(transcript + interimTranscript);
    }
  }, [transcript, interimTranscript, onTranscript]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore
        }
      }
    };
  }, []);

  // Browser not supported
  if (!isSupported) {
    return (
      <Button
        type="button"
        variant="outline"
        size="icon"
        disabled
        className={`border-zinc-700 opacity-50 cursor-not-allowed ${className}`}
        title="Voice input not supported in this browser"
        data-testid="voice-input-unsupported"
      >
        <MicOff className="w-5 h-5 text-zinc-500" />
      </Button>
    );
  }

  // Permission denied state
  if (permissionDenied) {
    return (
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={() => {
          setPermissionDenied(false);
          setError(null);
          toast.info('Please allow microphone access in your browser settings, then try again.');
        }}
        className={`border-red-500/30 hover:bg-red-500/10 ${className}`}
        title="Microphone access denied - click to retry"
        data-testid="voice-input-denied"
      >
        <AlertCircle className="w-5 h-5 text-red-400" />
      </Button>
    );
  }

  // Active listening state with transcript
  if (isListening || transcript) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {/* Live transcript display */}
        {(transcript || interimTranscript) && (
          <div className="flex-1 min-w-0 px-3 py-2 bg-zinc-800/50 rounded-lg border border-zinc-700 max-w-xs">
            <p className="text-sm text-white truncate" data-testid="voice-transcript">
              {transcript}
              <span className="text-zinc-400 italic">{interimTranscript}</span>
            </p>
          </div>
        )}
        
        {/* Cancel button */}
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={cancelListening}
          className="border-zinc-700 hover:bg-zinc-800 shrink-0"
          title="Cancel"
          data-testid="voice-cancel-btn"
        >
          <X className="w-4 h-4" />
        </Button>
        
        {/* Stop/Send button */}
        {isListening ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={stopListening}
            className="border-red-500/30 bg-red-500/10 hover:bg-red-500/20 shrink-0 relative"
            title="Stop recording"
            data-testid="voice-stop-btn"
          >
            <Square className="w-4 h-4 text-red-400" />
            {/* Pulse animation */}
            <span className="absolute inset-0 rounded-lg bg-red-500/20 animate-ping" />
          </Button>
        ) : transcript && (
          <Button
            type="button"
            onClick={handleManualSend}
            className="btn-oracle shrink-0"
            title="Send message"
            data-testid="voice-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        )}
      </div>
    );
  }

  // Default idle state - microphone button
  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      onClick={startListening}
      disabled={disabled}
      className={`border-zinc-700 hover:bg-primary/10 hover:border-primary/50 hover:text-primary transition-all ${className}`}
      title="Start voice input"
      data-testid="voice-input-btn"
    >
      <Mic className="w-5 h-5" />
    </Button>
  );
};

export default VoiceInput;
